/**
 * Plan validation. Two jobs:
 *   1. `validatePlan` — surface human-fixable problems for the UI (non-fatal).
 *   2. `isPlan` / `coercePlan` — guard untrusted JSON on import so we fail
 *      loudly with a specific message rather than silently into a broken state
 *      (spec §20).
 */
import type { Plan } from './types';

export interface ValidationIssue {
  path: string;
  message: string;
  severity: 'error' | 'warning';
}

export function validatePlan(plan: Plan): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const push = (path: string, message: string, severity: 'error' | 'warning' = 'error') =>
    issues.push({ path, message, severity });

  if (plan.household.people.length < 1 || plan.household.people.length > 2) {
    push('household.people', 'A household must have 1 or 2 people.');
  }

  const personIds = new Set(plan.household.people.map((p) => p.id));

  plan.household.people.forEach((p, i) => {
    if (p.birthYear < 1900 || p.birthYear > 2030) push(`people[${i}].birthYear`, 'Birth year looks wrong.');
    if (p.birthMonth < 1 || p.birthMonth > 12) push(`people[${i}].birthMonth`, 'Birth month must be 1-12.');
    if (p.retirementAge < 30 || p.retirementAge > 90) push(`people[${i}].retirementAge`, 'Retirement age out of range.', 'warning');
    if (p.lifeExpectancyAge < p.retirementAge) push(`people[${i}].lifeExpectancyAge`, 'Life expectancy is before retirement.');
    if (p.socialSecurity.claimingAge < 62 || p.socialSecurity.claimingAge > 70)
      push(`people[${i}].socialSecurity.claimingAge`, 'Claiming age must be 62-70.', 'warning');
  });

  plan.accounts.forEach((a, i) => {
    if (!personIds.has(a.ownerId)) push(`accounts[${i}].ownerId`, `Account "${a.label}" has no matching owner.`);
    if (a.balance < 0) push(`accounts[${i}].balance`, `Account "${a.label}" has a negative balance.`);
    if (a.expectedReturnOverride != null && (a.expectedReturnOverride < 0 || a.expectedReturnOverride > 0.15))
      push(`accounts[${i}].expectedReturnOverride`, 'Expected return override should be 0-15%.', 'warning');
    if (a.costBasis != null && a.costBasis < 0) push(`accounts[${i}].costBasis`, 'Cost basis cannot be negative.');
  });

  plan.incomes.forEach((inc, i) => {
    if (!personIds.has(inc.ownerId)) push(`incomes[${i}].ownerId`, `Income "${inc.label}" has no matching owner.`);
    if (inc.endAge < inc.startAge) push(`incomes[${i}]`, `Income "${inc.label}" ends before it starts.`);
  });

  if (!plan.assumptionProfiles.some((p) => p.label === plan.activeAssumptionLabel)) {
    push('activeAssumptionLabel', 'Active assumption profile does not exist.');
  }

  return issues;
}

/** Minimal structural guard for imported JSON. Not exhaustive — catches shape errors. */
export function isPlanShape(value: unknown): value is Plan {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.schemaVersion === 'number' &&
    typeof v.household === 'object' &&
    Array.isArray(v.accounts) &&
    Array.isArray(v.incomes) &&
    typeof v.budget === 'object'
  );
}
